import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { createComponent } from '@carvana/merch-components';
import classNames from 'classnames';
import React, { forwardRef, useContext, useRef, useState } from 'react';
import Slider from 'react-slick';
import Anchor from '@/common/Anchor';
import { useIsMobile } from '~/context/common/DeviceContext';
import usePreventVerticalScroll from '~/hooks/common/ui/usePreventVerticalScroll';
import Arrow from '~/icons/Arrow';
import styles from './Carousel.module.css';
import { SkeletonTextMedium, SkeletonTextSmall } from './Carousel.styles';
import { CarouselSettings } from './types';

export const IconWrapper = createComponent('div', {
  className: `flex h-full w-full items-center justify-center`
});

export const CarouselWrapper = createComponent('div', {
  className: styles.wrapper
});

type CarouselProps = React.PropsWithChildren<CarouselSettings> & {
  children?: React.ReactNode;
  testId?: string;
};

export default forwardRef<HTMLElement, CarouselProps>(
  function Carousel(props, ref) {
    const {
      arrows = true,
      centerMode = false,
      testId,
      children,
      className = '',
      dots = true,
      infinite = true,
      isSkeleton = false,
      heading,
      maxLength = 0,
      eventBaseName = '',
      sectionName = '',
      additionalCAs = {},
      subHeading,
      slidesToShow = 4,
      slidesToScroll = 1,
      viewLinkText,
      viewLinkUrl
    } = props;
    const { trackEvent } = useContext(AnalyticsContext);
    const [activeSlide, setActiveSlide] = useState(0);
    const sliderRef = useRef<Slider>(null);
    const sliderWrapperRef = useRef(null);
    const isMobile = useIsMobile();

    usePreventVerticalScroll(sliderWrapperRef);

    if (!maxLength) return null;

    function NavCarousel({ isLeft = false }: { isLeft?: boolean }) {
      const firstSlide = activeSlide === 0;
      const canRightNav = activeSlide + slidesToShow < maxLength;
      const outOfNavRange = (firstSlide && isLeft) || (!canRightNav && !isLeft);

      const handleClick = () => {
        if (isLeft && activeSlide !== 0) {
          sliderRef.current?.slickPrev();
        } else if (!isLeft && canRightNav) {
          sliderRef.current?.slickNext();
        }
      };

      return (
        <button
          type="button"
          className={classNames(
            'relative rounded-full border border-[#183558] bg-white m:h-44 m:w-44 l:h-48 l:w-48',
            {
              'border-grey-3': outOfNavRange,
              'text-grey-3': firstSlide && isLeft && !canRightNav,
              'bg-[#f6f7f9]': outOfNavRange,
              'animate-pulse border-[#F4F8FA]': isSkeleton
            }
          )}
          disabled={outOfNavRange}
          data-analytics-id={eventBaseName}
          data-testid={`Carousel nav: ${isLeft ? 'left' : 'right'}`}
          data-analytics-attributes={JSON.stringify({
            description: 'Carousel nav',
            sectionName: sectionName || heading,
            direction: isLeft ? 'left' : 'right',
            ...additionalCAs
          })}
          onClick={handleClick}
        >
          <IconWrapper>
            {isLeft ? (
              <Arrow
                className={classNames('text-inherit w-24 rotate-180', {
                  'fill-grey-3': firstSlide && isLeft,
                  'animate-pulse fill-[#F4F8FA]': isSkeleton
                })}
              />
            ) : (
              <Arrow
                className={classNames('text-inherit w-24', {
                  'fill-grey-3': !canRightNav,
                  'animate-pulse fill-[#F4F8FA]': isSkeleton
                })}
              />
            )}
          </IconWrapper>
        </button>
      );
    }

    const sliderSettings = {
      arrows,
      afterChange: (current: number) => {
        if (activeSlide + slidesToShow - 1 <= maxLength) {
          sliderRef.current?.slickGoTo(current);
          setActiveSlide(current);
        } else {
          sliderRef.current?.slickGoTo(activeSlide);
        }
      },
      onSwipe: () => {
        trackEvent(`Swiped - ${eventBaseName}`, {
          description: 'Carousel Nav',
          sectionName: sectionName || heading,
          ...additionalCAs
        });
      },
      centerMode,
      className,
      dots,
      infinite,
      speed: 500,
      slidesToShow,
      slidesToScroll
    };

    const showHeading = Boolean(
      !isMobile || isSkeleton || heading || subHeading || viewLinkUrl
    );

    return (
      <div data-testid={testId}>
        {showHeading && (
          <article
            className="mb-16 flex w-full items-start justify-between m:mb-24 m:items-center"
            ref={ref}
          >
            <div className="flex flex-col items-start gap-4 m:gap-8">
              <h2 className="text-blue-6 tl-header-s s:tl-header-m l:tl-header-xl">
                {isSkeleton ? (
                  <SkeletonTextMedium className="w-[120px] s:w-[220px]" />
                ) : (
                  heading
                )}
              </h2>
              {subHeading && (
                <h3>
                  <small className="flex text-grey-5 s:tl-body-m l:tl-body-l">
                    {isSkeleton ? (
                      <SkeletonTextSmall className="w-[100px] s:w-[160px]" />
                    ) : (
                      subHeading
                    )}
                  </small>
                </h3>
              )}
              {viewLinkUrl && (
                <Anchor
                  className="!no-underline tl-link-m"
                  href={viewLinkUrl}
                  data-analytics-id={`${eventBaseName} - View more results`}
                  data-analytics-attributes={JSON.stringify({
                    sectionName: sectionName || heading,
                    viewLinkUrl
                  })}
                  data-testid="carousel-view-more"
                >
                  {viewLinkText ? viewLinkText : 'View More'}
                </Anchor>
              )}
            </div>
            {arrows && (
              <div className="text-blue-6">
                <div className="hidden justify-between m:flex m:w-[100px] l:w-[112px]">
                  <NavCarousel isLeft />
                  <NavCarousel />
                </div>
              </div>
            )}
          </article>
        )}
        <CarouselWrapper ref={sliderWrapperRef}>
          <Slider ref={sliderRef} {...sliderSettings}>
            {children}
          </Slider>
        </CarouselWrapper>
      </div>
    );
  }
);
